import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { formatRelativeTime } from '../utils/timeFormat';
import './MentorProfilePage.css';

const SPORT_EMOJIS = {
  'Basketball': '🏀',
  'Football': '🏈',
  'Soccer': '⚽',
  'Baseball/Softball': '⚾',
  'Tennis': '🎾',
  'Volleyball': '🏐',
  'Wrestling': '🤼',
  'Golf': '⛳',
};

function parseSports(sportVal) {
  if (!sportVal) return [];
  try {
    const parsed = JSON.parse(sportVal);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch {}
  return sportVal.split(',').map((s) => s.trim()).filter(Boolean);
}

function StarDisplay({ value, size = 'md' }) {
  return (
    <span className={`star-display star-display-${size}`} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= Math.round(value) ? 'star filled' : 'star empty'}>★</span>
      ))}
    </span>
  );
}

function MentorProfilePage() {
  const { mentorId } = useParams();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mentor, setMentor] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: mentorData, error: mentorErr } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, sport, secondary_sport, focus_areas, city, state, grad_year, hourly_rate, avatar_url, bio, role')
          .eq('id', mentorId)
          .eq('role', 'mentor')
          .single();

        if (mentorErr || !mentorData) {
          setError('Mentor not found.');
          setLoading(false);
          return;
        }
        setMentor(mentorData);

        const { data: ratingsData } = await supabase
          .from('ratings')
          .select('rating, comment, created_at, parent:profiles!ratings_parent_id_fkey(first_name, last_name)')
          .eq('mentor_id', mentorId)
          .order('created_at', { ascending: false });

        setRatings(ratingsData || []);
      } catch (err) {
        setError(err.message || 'Failed to load mentor profile.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [mentorId]);

  const avgRating = ratings.length > 0
    ? ratings.reduce((s, r) => s + r.rating, 0) / ratings.length
    : 0;

  const handleRequestSession = () => {
    if (!user) {
      navigate('/signin', { state: { from: { pathname: `/request-session/${mentorId}` } } });
      return;
    }
    if (profile?.role === 'mentor') return;
    navigate(`/request-session/${mentorId}`);
  };

  if (loading) return <div className="app-loading"><div className="app-loading-spinner" /></div>;
  if (error || !mentor) return (
    <div className="page-container">
      <div className="alert alert-error">{error || 'Mentor not found.'}</div>
      <Link to="/mentors" className="btn btn-ghost" style={{ marginTop: '1rem' }}>Back to Mentors</Link>
    </div>
  );

  const isSelf = user?.id === mentorId;
  const isParent = profile?.role === 'parent' || !user;
  const showRequestBtn = !isSelf && isParent;
  const focusAreas = mentor.focus_areas || [];

  const sports = parseSports(mentor.sport);
  if (mentor.secondary_sport && !sports.includes(mentor.secondary_sport)) {
    sports.push(mentor.secondary_sport);
  }

  return (
    <div className="mentor-profile-page">
      <div className="mentor-profile-inner">
        <Link to="/mentors" className="mentor-profile-back">← Back to Trainers</Link>

        <div className="mentor-profile-layout">

          {/* Sidebar */}
          <aside className="mentor-profile-sidebar">
            <div className="mentor-profile-photo-wrap">
              {mentor.avatar_url ? (
                <img
                  src={mentor.avatar_url}
                  alt={`${mentor.first_name} ${mentor.last_name}`}
                  className="mentor-profile-photo"
                />
              ) : (
                <div className="mentor-profile-photo-placeholder" aria-hidden="true">
                  {mentor.first_name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
            </div>

            {ratings.length > 0 && (
              <div className="mentor-profile-rating-summary">
                <StarDisplay value={avgRating} size="lg" />
                <span className="mentor-profile-avg">{avgRating.toFixed(1)}</span>
                <span className="mentor-profile-rating-count">
                  {ratings.length} review{ratings.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            {mentor.hourly_rate != null && (
              <div className="mentor-profile-rate">
                <span className="mentor-profile-rate-value">${Number(mentor.hourly_rate).toFixed(0)}</span>
                <span className="mentor-profile-rate-label">per hour (in person)</span>
              </div>
            )}

          {!user && (
            <>
              <Link
                to={`/signup?role=parent&redirect=/request-session/${mentorId}`}
                className="btn btn-primary btn-full mentor-profile-cta"
              >
                Sign Up to Book
              </Link>
              <p className="mentor-profile-signin-note">
                Already have an account?{' '}
                <Link to="/signin" state={{ from: { pathname: `/request-session/${mentorId}` } }}>
                  Sign in
                </Link>
              </p>
            </>
          )}

          {user && showRequestBtn && (
            <button
              className="btn btn-primary btn-full mentor-profile-cta"
              onClick={handleRequestSession}
            >
              Request a Session
            </button>
          )}
          </aside>

          {/* Main */}
          <div className="mentor-profile-main">
            <div className="mentor-profile-info">

              {/* Name */}
              <h1 className="mentor-profile-name">{mentor.first_name} {mentor.last_name}</h1>

              {/* Location + grad year */}
              {(mentor.city || mentor.state) && (
                <div className="mentor-profile-location-row">
                  <i className="ti ti-map-pin" aria-hidden="true"></i>
                  <span>{[mentor.city, mentor.state].filter(Boolean).join(', ')}</span>
                </div>
              )}

              {mentor.grad_year && (
                <div className="mentor-profile-school-row">
                  <i className="ti ti-school" aria-hidden="true"></i>
                  <span>Class of {mentor.grad_year}</span>
                </div>
              )}

              <div className="mentor-profile-divider" />

              {/* Sports */}
              {sports.map((sport) => (
                <div key={sport} className="mentor-profile-sport-row">
                  <span className="mentor-profile-sport-emoji">{SPORT_EMOJIS[sport] || '🏅'}</span>
                  <span className="mentor-profile-sport-name">{sport}</span>
                </div>
              ))}

              <div className="mentor-profile-divider" />

              {/* Focus areas */}
              {focusAreas.length > 0 && (
                <div className="mentor-profile-focus-section">
                  <h2 className="mentor-profile-section-title">Training Focus Areas</h2>
                  <div className="mentor-profile-focus-tags">
                    {focusAreas.map((area) => (
                      <span key={area} className="focus-tag">{area}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Bio */}
              {mentor.bio && (
                <div className="mentor-profile-bio-section">
                  <h2 className="mentor-profile-section-title">About</h2>
                  <p className="mentor-profile-bio">{mentor.bio}</p>
                </div>
              )}
            </div>

            {/* Reviews */}
            <div className="mentor-profile-reviews">
              <h2 className="mentor-profile-section-title">
                Reviews {ratings.length > 0 && `(${ratings.length})`}
              </h2>
              {ratings.length === 0 ? (
                <p className="mentor-profile-no-reviews">
                  No reviews yet. Be the first to train with {mentor.first_name} {mentor.last_name}!
                </p>
              ) : (
                <div className="mentor-profile-review-list">
                  {ratings.map((r, idx) => (
                    <div key={idx} className="review-card card">
                      <div className="review-header">
                        <div>
                          <span className="review-author">
                            {r.parent
                              ? `${r.parent.first_name} ${r.parent.last_name.charAt(0)}.`.trim()
                              : 'A parent'}
                          </span>
                          <StarDisplay value={r.rating} size="sm" />
                        </div>
                        <span className="review-date">{formatRelativeTime(r.created_at)}</span>
                      </div>
                      {r.comment && <p className="review-comment">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default MentorProfilePage;
